<script setup lang="ts">
import { inject, Ref, ref, onBeforeMount, computed } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { TOTAL_COST } from '@/constants';
import { formatDate } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';
import { useWorksheetComposable } from './useWorksheetComposable';
import omit from 'lodash/omit';
import cloneDeep from 'lodash/cloneDeep';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import RadioButton from 'primevue/radiobutton';
import TableColumn from 'primevue/column';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

const dialogRef = inject('dialogRef') as Ref;
const roomData = dialogRef?.value?.data?.roomData;
const onApply = dialogRef?.value?.options?.emits?.onApply;

const numberDynamicColumns = ref<any>([]);
const currentLanguage = getCurrentLocale();
const editingRowsHotel = ref({});
const editRateOfChange = ref({
  isEdit: false,
  data: +roomData.rateOfChange
});
const editDuration = ref({
  isEdit: false,
  data: +roomData.duration
});
const { authorizations, filterCostAdjustment, PRICE_KEY, titleCostAdjustmentTool } = useWorksheetComposable();
const isLoading = ref(false);
const oldChildValue = ref([]);
const listPrice = ref([]);
const calculatedData = ref({
  duration: roomData.duration,
  rateOfChange: +roomData.rateOfChange
});

const checkCurrentLanguage = computed(() => {
  return currentLanguage == 'fr' ? 'fr-FR' : 'en-US';
});

const handleSaveRateOfChange = (rateOfChange: number, roomData: any) => {
  roomData.rateOfChange = rateOfChange ? rateOfChange?.toFixed(4) : 1;
  calculatedData.value.rateOfChange = rateOfChange ? +rateOfChange?.toFixed(4) : 1;
  isLoading.value = true;
  roomData.rooms?.forEach((room: any) => {
    const cloneDefaultHotelCostCAD = cloneDeep(room.price.defaultHotelCostCAD);
    calculateHotelCost(cloneDefaultHotelCostCAD, room, rateOfChange);
    room.price.hotelCostCAD = cloneDefaultHotelCostCAD;
  });
  handleRenderData();
  editRateOfChange.value.isEdit = false;
  isLoading.value = false;
};

const handleSaveDuration = (duration: number, roomData: any) => {
  roomData.duration = duration;
  calculatedData.value.duration = duration;
  isLoading.value = true;
  roomData.rooms?.forEach((room, roomIndex) => {
    room.price.supplierCostCAD = calculatedByPrices(room.isPerPerson, listPrice.value[roomIndex], calculatedData.value);
  });
  handleRenderData();
  editDuration.value.isEdit = false;
  isLoading.value = false;
};

const calculateHotelCost = (hotelCost, room, rateOfChange) => {
  for (const key in hotelCost) {
    if (Array.isArray(room.price.defaultHotelCostCAD[key])) {
      hotelCost[key] = hotelCost[key].map((roomPrice) => (+roomPrice * +rateOfChange).toFixed(2));
    } else {
      hotelCost[key] = (+hotelCost[key] * +rateOfChange).toFixed(2);
    }
  }
};

const onClickApply = () => {
  const listCostAdjustment = [];
  for (let roomIndex = 0; roomIndex <= roomData.rooms.length - 1; roomIndex++) {
    listPrice.value[roomIndex].forEach((price: any) => {
      if (price.rowName == 'costAdj') {
        const oldPrice = omit(price, ['key']);
        convertString(oldPrice);
        listCostAdjustment.push(oldPrice);
      }
    });
  }
  onApply(listCostAdjustment);
  dialogRef?.value.close();
};

const convertString = (price: any) => {
  for (const key in price) {
    if (price[key] === 'N/A') {
      price[key] = '0.00';
    } else if (Array.isArray(price[key])) {
      price[key] = price[key].map((item) => (item === 'N/A' ? '0.00' : item));
    }
  }
};

const covertOccupanciesNameTable = (rowName: string) => {
  return titleCostAdjustmentTool.find((title) => title.key === rowName)?.name;
};

const replaceNullsValue = (dataValue) => {
  for (const key in dataValue) {
    if (dataValue[key] === null) {
      dataValue[key] = 0;
    } else if (Array.isArray(dataValue[key])) {
      dataValue[key] = dataValue[key].map((item) => (item === null ? 0 : item));
    }
  }
};

const onRowEditSaveHotel = ({ newData }, roomIndex: number) => {
  isLoading.value = true;
  const newCloneData = cloneDeep(newData);
  replaceNullsValue(newCloneData);
  if (newCloneData?.rowName == 'supplierCost' || newCloneData?.rowName == 'supplierTax' || newCloneData?.rowName == 'supplierSupplement') {
    roomData.rooms[roomIndex].price[newCloneData.rowName] = newCloneData;
    const getIndex = listPrice.value[roomIndex].findIndex((price: any) => price.rowName === newCloneData.rowName);
    listPrice.value[roomIndex][getIndex] = newCloneData;
  }
  renderTableCalculate();
  roomData.rooms[roomIndex].price.supplierCostCAD = calculatedByPrices(roomData.rooms[roomIndex].isPerPerson, listPrice.value[roomIndex], calculatedData.value);
  renderTableCalculate();
  isLoading.value = false;
};

const onRowEditCancel = (event, listValue: any) => {
  for (let i = 0; i < listValue[event.index].childPrices.length; i++) {
    listValue[event.index].childPrices[i] = oldChildValue.value[i];
  }
};

const getChildPrices = (data: any) => {
  oldChildValue.value = cloneDeep(data.childPrices);
};

const calculatedCAD = (isPerPerson, calculatedValue, roomNumber, calculatedData) => {
  if (isPerPerson) {
    return (+calculatedValue * +calculatedData.duration * +calculatedData.rateOfChange).toFixed(2);
  } else {
    return ((+calculatedValue * +calculatedData.duration * +calculatedData.rateOfChange) / roomNumber).toFixed(2) ?? 'N/A';
  }
};

const calculateCADChild = (isPerPerson, calculatedValue, calculatedData) => {
  return !isPerPerson ? 'N/A' : ((+calculatedValue * +calculatedData.duration * +calculatedData.rateOfChange).toFixed(2) ?? 'N/A');
};

const calculatePriceWithKey = (keyList, isPerPerson, calculatedData) => {
  const supplierTotalObject = {};
  for (const key in keyList) {
    switch (key) {
      case 'singlePrice':
        supplierTotalObject.singlePrice = calculatedCAD(isPerPerson, keyList[key], 1, calculatedData);
        break;
      case 'doublePrice':
        supplierTotalObject.doublePrice = calculatedCAD(isPerPerson, keyList[key], 2, calculatedData);
        break;
      case 'triplePrice':
        supplierTotalObject.triplePrice = calculatedCAD(isPerPerson, keyList[key], 3, calculatedData);
        break;
      case 'quadPrice':
        supplierTotalObject.quadPrice = calculatedCAD(isPerPerson, keyList[key], 4, calculatedData);
        break;
      case 'fiveInRoomPrice':
        supplierTotalObject.fiveInRoomPrice = calculatedCAD(isPerPerson, keyList[key], 5, calculatedData);
        break;
      case 'sixInRoomPrice':
        supplierTotalObject.sixInRoomPrice = calculatedCAD(isPerPerson, keyList[key], 6, calculatedData);
        break;
      case 'infantPrice':
        if (isPerPerson) {
          supplierTotalObject.infantPrice = calculatedCAD(isPerPerson, keyList[key], 1, calculatedData);
        } else {
          supplierTotalObject.infantPrice = 'N/A';
        }
        break;
      case 'childOnePrice':
        supplierTotalObject.childOnePrice = 'N/A';
        break;
      case 'childTwoPrice':
        supplierTotalObject.childTwoPrice = 'N/A';
        break;
      case 'childThreePrice':
        supplierTotalObject.childThreePrice = 'N/A';
        break;
      case 'childPrices':
        supplierTotalObject.childPrices = [];
        if (keyList[key].length) {
          for (let child = 0; child <= keyList[key].length - 1; child++) {
            supplierTotalObject.childPrices.push(calculateCADChild(isPerPerson, keyList[key][child], calculatedData));
          }
        }
        break;
    }
  }
  supplierTotalObject.rowName = 'supplierCostCAD';
  supplierTotalObject.canEdit = false;
  return supplierTotalObject;
};

const calculatedByPrices = (isPerPerson, supplierData, calculatedData) => {
  const getSupplierTotal = supplierData.find((price) => price.rowName == 'supplierTotal');
  if (getSupplierTotal) {
    const keyList = omit(getSupplierTotal, ['rowName', 'canEdit']);
    return calculatePriceWithKey(keyList, isPerPerson, calculatedData);
  }
};

const renderTableCalculate = () => {
  listPrice.value = [];
  roomData.rooms?.forEach((room: any) => {
    listPrice.value.push(filterCostAdjustment(room.price, authorizations.quote_management, room.isPerPerson, calculatedData.value));
  });
};

const handleRenderData = () => {
  isLoading.value = true;
  roomData.rooms?.forEach((room: any, index: number) => {
    listPrice.value.push(filterCostAdjustment(room.price, authorizations.quote_management, room.isPerPerson, calculatedData.value));
    numberDynamicColumns.value.push(listPrice.value?.[index][0].childPrices?.length);
    room.price.supplierCostCAD = calculatedByPrices(room.isPerPerson, listPrice.value[index], calculatedData.value);
  });
  renderTableCalculate();
  isLoading.value = false;
};

const changeRadio = () => {
  isLoading.value = true;
  handleRenderData();
  isLoading.value = false;
};

onBeforeMount(() => {
  handleRenderData();
});
</script>
<template>
  <div class="quote_calculation">
    <div class="option" id="hotelInfo">
      <div class="lead-option py-2" id="header">{{ $t('worksheet.cost_calculation') }}</div>
      <div class="detail-option flex justify-content-between flex-wrap px-4 pt-2">
        <div>
          <div class="hotel font-bold mb-2">
            <div class="flex gap-3">
              <div class="min-w-label">{{ $t('worksheet.hotel') }}:</div>
              <div class="font-normal">{{ roomData.hotel }}</div>
            </div>
          </div>
          <div class="hotel-rate-of-change font-bold mb-2">
            <div class="flex gap-3" v-if="!editRateOfChange.isEdit">
              <div class="min-w-label">{{ $t('worksheet.rate_of_exchange') }}:</div>
              <div class="font-normal">{{ +roomData.rateOfChange }}</div>
              <span class="text-center mr-4">
                <i class="pi pi-pencil cursor-pointer" @click="editRateOfChange = { isEdit: true, data: +roomData.rateOfChange }"></i>
              </span>
            </div>
            <div class="flex" v-else>
              <div class="min-w-label">{{ $t('worksheet.rate_of_exchange') }}:</div>
              <div class="w-6rem">
                <InputNumber :locale="checkCurrentLanguage" :minFractionDigits="2" :maxFractionDigits="4" class="input-rate-of-change" mode="decimal" v-model="editRateOfChange.data" :min="0" />
              </div>
              <div class="text-center ml-2 flex gap-2 align-items-center">
                <i class="pi pi-check cursor-pointer" @click="handleSaveRateOfChange(editRateOfChange.data, roomData)"></i>
                <i class="pi pi-times cursor-pointer" @click="editRateOfChange = { isEdit: false, data: roomData.rateOfChange }"></i>
              </div>
            </div>
          </div>
          <div class="hotel font-bold mb-2">
            <div class="flex gap-3">
              <div class="min-w-label">{{ $t('proof_pricing.currency') }}:</div>
              <div class="font-normal">{{ roomData.currency }}</div>
            </div>
          </div>
        </div>
        <div class="flex flex-column">
          <div class="hotel font-bold mb-2">
            <div class="flex gap-3">
              <div class="min-w-label">{{ $t('client_copy.dep_date') }}:</div>
              <div class="font-normal">{{ formatDate(roomData.dateDep, 'dd-LLL-yyyy', false, currentLanguage) }}</div>
            </div>
          </div>
          <div class="hotel-duration font-bold mb-2">
            <div class="flex gap-3" v-if="!editDuration.isEdit">
              <div class="min-w-label">{{ $t('worksheet.duration') }}:</div>
              <div class="font-normal">{{ +roomData.duration }}</div>
              <span class="text-center mr-4">
                <i class="pi pi-pencil cursor-pointer duration-edit" @click="editDuration = { isEdit: true, data: +roomData.duration }"></i>
              </span>
            </div>
            <div class="flex" v-else>
              <div class="min-w-label">{{ $t('worksheet.duration') }}:</div>
              <div class="w-6rem">
                <InputNumber :locale="checkCurrentLanguage" class="input-hotel-duration" mode="decimal" v-model="editDuration.data" :min="0" />
              </div>
              <div class="text-center ml-2 flex gap-2 align-items-center">
                <i class="pi pi-check cursor-pointer" @click="handleSaveDuration(editDuration.data, roomData)"></i>
                <i class="pi pi-times cursor-pointer" @click="editDuration = { isEdit: false, data: +roomData.duration }"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-for="(room, roomIndex) in roomData.rooms" :key="roomIndex">
      <div class="pricing">
        <div class="lead-pricing flex align-items-center justify-content-between">{{ getTextVariant(currentLanguage, room.longName, room.longNameFr) }} ({{ room.code }})</div>
        <div class="pricing-detail">
          <DataTable
            id="hotel-table"
            rowGroupMode="subheader"
            v-model:editingRows="editingRowsHotel[roomIndex]"
            :value="listPrice[roomIndex]"
            class="w-full"
            editMode="row"
            data-key="rowName"
            @row-edit-save="onRowEditSaveHotel($event, roomIndex)"
            @row-edit-cancel="onRowEditCancel($event, listPrice[roomIndex])"
            showGridlines
          >
            <TableColumn header="" dataType="string" class="relative">
              <template #body="{ data }">
                <div :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'text-900 font-bold']">
                  <span>{{ covertOccupanciesNameTable(data.rowName) }}</span>
                  <div v-if="data.rowName == 'occupanciesEdit'" class="selected-radio flex justify-content-end align-items-center">
                    <div class="flex align-items-center">
                      <label for="perPerson" class="mr-1">{{ $t('worksheet.per_person') }}</label>
                      <RadioButton id="radio-person" v-model="room.isPerPerson" inputId="perPerson" name="calculatedBy" :value="true" @change="changeRadio()" />
                    </div>
                    <div class="flex align-items-center ml-3">
                      <label for="perRoom" class="mr-1">{{ $t('worksheet.per_room') }}</label>
                      <RadioButton id="radio-room" v-model="room.isPerPerson" inputId="perRoom" name="calculatedBy" :value="false" @change="changeRadio()" />
                    </div>
                  </div>
                </div>
              </template>
            </TableColumn>
            <TableColumn field="singlePrice" :header="$t('book_form.single')" dataType="number" class="relative">
              <template #body="{ data, field, index }">
                <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                  <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                </div>
              </template>
              <template #editor="{ data, field, index }">
                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" :locale="checkCurrentLanguage" class="w-full" />
              </template>
            </TableColumn>
            <TableColumn field="doublePrice" :header="$t('book_form.double')" dataType="number" class="relative">
              <template #body="{ data, field, index }">
                <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                  <span :class="data.key === PRICE_KEY.HEADER && 'hidden'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                </div>
              </template>
              <template #editor="{ data, field, index }">
                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" :locale="checkCurrentLanguage" class="w-full" />
              </template>
            </TableColumn>
            <TableColumn field="triplePrice" :header="$t('book_form.triple')" dataType="number" class="relative">
              <template #body="{ data, field, index }">
                <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                  <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                </div>
              </template>
              <template #editor="{ data, field, index }">
                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" :locale="checkCurrentLanguage" class="w-full" />
              </template>
            </TableColumn>
            <TableColumn field="quadPrice" :header="$t('book_form.quad')" dataType="number" class="relative w-7rem">
              <template #body="{ data, field, index }">
                <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                  <span :class="data.key === PRICE_KEY.HEADER && 'hidden'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                </div>
              </template>
              <template #editor="{ data, field, index }">
                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" :locale="checkCurrentLanguage" class="w-full" />
              </template>
            </TableColumn>
            <TableColumn field="fiveInRoomPrice" :header="$t('book_form.5_in_room')" dataType="number" class="relative">
              <template #body="{ data, field, index }">
                <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                  <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                </div>
              </template>
              <template #editor="{ data, field, index }">
                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" :locale="checkCurrentLanguage" class="w-full" />
              </template>
            </TableColumn>
            <TableColumn field="sixInRoomPrice" :header="$t('book_form.6_in_room')" dataType="number" class="relative w-7rem">
              <template #body="{ data, field, index }">
                <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                  <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                </div>
              </template>
              <template #editor="{ data, field, index }">
                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" :locale="checkCurrentLanguage" class="w-full" />
              </template>
            </TableColumn>
            <TableColumn field="childPrices" :header="$t('common.child')" class="min-w-8rem relative parent-column" v-for="(column, indexColumn) in numberDynamicColumns[roomIndex]" :key="'childColumn' + indexColumn">
              <template #body="{ data, field, index }">
                <div v-if="index === 0 || true" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header ']">
                  <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">
                    {{
                      data.rowName && data.rowName === 'occupanciesEdit'
                        ? data[field]?.[indexColumn]
                        : Number(data[field]?.[indexColumn])
                          ? Number(data[field]?.[indexColumn]).toFixed(2)
                          : data[field]?.[indexColumn] == 'N/A'
                            ? data[field]?.[indexColumn]
                            : '0.00'
                    }}
                  </span>
                </div>
                <div v-else :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header ']">
                  <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                </div>
              </template>
              <template #editor="{ data, field, index }">
                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field][indexColumn]" :locale="checkCurrentLanguage" class="w-full" />
                <InputText v-else v-model="data[field][indexColumn]" class="w-full" />
              </template>
            </TableColumn>
            <TableColumn field="infantPrice" :header="$t('common.infant')" dataType="number" class="relative">
              <template #body="{ data, field, index }">
                <div v-if="index === 0">
                  {{ data[field] }}
                </div>
                <div v-else :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                  <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? (data[field] != 'N/A' ? Number(data[field]).toFixed(2) : data[field] || data[field]) : '0.00' }}</span>
                </div>
              </template>
              <template #editor="{ data, field, index }">
                <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" :locale="checkCurrentLanguage" class="w-full" />
                <InputText v-else v-model="data[field]" class="w-full" />
              </template>
            </TableColumn>
            <TableColumn :rowEditor="true" class="text-center relative w-5rem" :header="$t('common.action')">
              <template #body="{ editorInitCallback, data }">
                <div v-if="data.rowName !== 'occupanciesEdit'" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header right-0']">
                  <span class="edit-span" v-if="data.key !== TOTAL_COST && data.key !== PRICE_KEY.HEADER && data.canEdit" @click="getChildPrices(data)"><i class="pi pi-pencil" @click="editorInitCallback"></i></span>
                </div>
                <p v-if="data.key === TOTAL_COST || data.key === PRICE_KEY.HEADER" class="opacity-0" :class="{ hidden: data.rowName === 'additionInformation' }">{{ $t('common.edit') }}</p>
              </template>
              <template #editor="{ editorSaveCallback, editorCancelCallback }">
                <div class="flex">
                  <i class="pi pi-check edit-pi" @click="editorSaveCallback"></i>
                  <i class="pi pi-times edit-pi" @click="editorCancelCallback"></i>
                </div>
              </template>
            </TableColumn>
          </DataTable>
        </div>
      </div>
    </div>
    <div class="flex flex-column align-items-center justify-content-center mt-3">
      <div class="noti_text mb-2 text-600 text-sm mb-1 hint font-italic text-primary">{{ $t('worksheet.warning_txt') }}</div>
      <Button id="tool-btn-save" :label="$t('worksheet.save_and_apply')" icon="pi pi-check" @click="onClickApply" />
    </div>
    <LoadingSpinner v-if="isLoading" />
  </div>
</template>
<style scoped lang="scss">
:deep() {
  .p-inputtextarea:disabled {
    opacity: 1;
    cursor: not-allowed;
  }
  .price-header {
    position: absolute;
    top: 0px;
    right: -3px;
    left: -3px;
    bottom: 0;
  }

  .background-opacity {
    background: rgba($color: #ddd, $alpha: 0.5);
  }

  .min-w-10rem {
    min-width: 10rem;
  }
  .min-w-8rem {
    min-width: 8rem;
  }

  .min-w-5rem {
    min-width: 5rem;
  }

  .flight-detail {
    input {
      width: 100%;
    }
    table {
      td,
      th {
        padding: 1rem 0.75rem !important;
      }
    }
  }
}

.table-action-button {
  height: 2rem;
  width: 2rem;
  line-height: 2rem;
}

.add-flight-button {
  color: $col-primary;
  text-decoration: underline;
  cursor: pointer;
}
.word-break {
  word-break: normal !important;
}
#header {
  background-color: $col-primary;
  color: $col-white;
  padding: 0rem 1.2rem;
}

.lead-promotions,
.lead-comment,
.lead-additional-hotel,
.lead-option,
.lead-flight,
.lead-pricing {
  background-color: $col-greydk;
  color: $col-white;
  padding: 0.8rem 1.2rem;
  margin-top: 1rem;
  font-weight: 700;

  .custom-buttom,
  .process-button {
    background-color: $col-primary;
    color: $col-white;
    font-weight: bold;
  }
}

.lead-option {
  background-color: $col-primary;
}

.top-2px {
  top: 2px;
}
.width-220px {
  width: 220px;
}

.height-150px {
  height: 150px;
}

.min-w-200px {
  min-width: 200px;
}

.pricing {
  table {
    border-collapse: collapse;
    width: 100%;
  }

  td,
  th {
    border: $border-greybd;
    text-align: left;
    padding: 8px;
    font-weight: 400;
  }

  tr:nth-child(even) {
    background-color: #dddddd;
  }

  tr:first-child th {
    font-weight: 700;
  }

  tr td:first-child {
    font-weight: 700;
  }

  tr:last-child {
    background-color: $col-black;
    color: $col-white;

    td {
      border: 1px solid $col-white;
    }
  }
}

.edit-pi {
  width: 40px;
  height: 40px;
  line-height: 40px;
}

.min-w-label {
  min-width: 16rem;
}

.min-w-label--new {
  min-width: 12rem;
}

:deep(.p-inputnumber .p-inputnumber-input) {
  width: 100%;
}

@keyframes loader {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.rotage {
  animation: loader infinite 0.8s linear;
}

.p-button.p-component {
  padding: 0.45rem 0.95rem;
}

.modal {
  width: min(500px, 80vw);
}

.wrapper {
  padding: 0.8rem;
  border: 1px solid #ccc;
  border-radius: 0.5rem;
}

.min-w-5rem {
  min-width: 5.5rem;
}
</style>
